import React, { useState, useContext } from "react";
import { Box, Typography, Button, TextField } from "@mui/material";
import { useTranslation } from "react-i18next";
import AddIcon from '@mui/icons-material/Add';
import { AlertContext } from "@context/AlertProvider";
import { useNotificationClubCreateMutation, useNotificationSiteCreateMutation } from "@mutation/notificationMutation";
import { ClubSiteTargetObject, SelectClubSiteTarget } from "@component/SelectClubSiteTarget";
import { removeEmptyValues } from '@utils/generalUtils';

export default function NotificationsPage() {
  const { t } = useTranslation(["main", "notifications", "error"]);
  const { addAlertBanner } = useContext(AlertContext);
  const [target, setTarget] = useState(new ClubSiteTargetObject(null, null));
  const [formData, setFormData] = useState({
    title: "",
    body: ""
  });

  const notificationClubCreateMutation = useNotificationClubCreateMutation();
  const notificationSiteCreateMutation = useNotificationSiteCreateMutation();

  const handleCreateClick = () => {
    if (!formData.title.trim() || !formData.body.trim()) {
      addAlertBanner(t("notifications:emptyFieldsError"), "error");
      return;
    }

    const cleaned = removeEmptyValues(formData);

    const handleMutationError = (error) => {
      if (!error.response) {
        addAlertBanner(t("error:networkError"), "error", 4000);
        return;
      }

      const statusCode = error.response.status;
      const errorDetail = error.response.data?.detail;

      if (statusCode === 403) {
        addAlertBanner(
          target.club_uuid
            ? t("error:noClubCreationPrivileges")
            : t("error:noSiteCreationPrivileges"),
          "error",
          5000
        );
      } else if (statusCode === 400) {
        addAlertBanner(
          errorDetail || t("error:creationValidationError"),
          "error",
          5000
        );
      } else if (statusCode === 404) {
        addAlertBanner(
          target.club_uuid
            ? t("error:clubNotFound")
            : t("error:siteNotFound"),
          "error",
          4000
        );
      } else if (statusCode >= 500) {
        addAlertBanner(t("error:serverError"), "error", 4000);
      } else {
        addAlertBanner(t("error:somethingGoesWrong"), "error", 4000);
      }
    };

    // Create new challenge
    if (target.club_uuid) {
      notificationClubCreateMutation.mutate(
        { data: { ...cleaned, club_uuid: target.club_uuid } },
        {
          onSuccess: () => {
            addAlertBanner(t("main:saveSuccess"), "success");
            setFormData({ title: "", body: "" });
            setTarget(new ClubSiteTargetObject(null, null));
          },
          onError: handleMutationError
        }
      );
    } else if (target.site_uuid) {
      notificationSiteCreateMutation.mutate(
        { data: { ...cleaned, site_uuid: target.site_uuid } },
        {
          onSuccess: () => {
            addAlertBanner(t("main:saveSuccess"), "success");
            setFormData({ title: "", body: "" });
            setTarget(new ClubSiteTargetObject(null, null));
          },
          onError: handleMutationError
        }
      );
    } else {
      addAlertBanner(t("error:selectTargetError"), "error", 4000);
    }
  };

  return (
    <Box
      sx={{
        width: { xs: "100%", sm: "95%", md: "95%" },
        display: "flex",
        flexDirection: "column",
        gap: 3,
        mx: "auto",
        pb: 4,
        pt: 4
      }}
    >
      <Box sx={{
        flex: 1,
        minWidth: 0
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{display: "flex", flexDirection: "column"}}>
          <Typography variant="h2" mb={1}>
            {t('notifications:notifications')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t('notifications:pageSubtitle')}
          </Typography>
          </Box>
          <Button
            startIcon={<AddIcon />}
            variant="contained"
            onClick={handleCreateClick}
            sx={{ mb: 2 }}
            disabled={
              notificationClubCreateMutation.isLoading ||
              notificationSiteCreateMutation.isLoading
            }
          >
            {t('notifications:createNotification')}
          </Button>
        </Box>

        <SelectClubSiteTarget
          target={target}
          setTarget={setTarget}
        />

        <TextField
          label={t('notifications:title')}
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          fullWidth
          margin="normal"
          required
          sx={{
            mt: 3,
            '& .MuiInputLabel-root': {
              top: '-8px',  // Adjust label position
            }
          }}
        />

        <TextField
          label={t('notifications:body')}
          value={formData.body}
          onChange={(e) => setFormData({ ...formData, body: e.target.value })}
          fullWidth
          margin="normal"
          multiline
          rows={4}
          required
          sx={{
            mt: 3,
            '& .MuiInputLabel-root': {
              top: '-8px',  // Adjust label position
            }
          }}
        />
      </Box>
    </Box>
  );
}